import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAxiosInstance } from "../../../services/Interceptor";
import { likeAthlete } from "../../../services/AthleteService";
import {
  toggleFavouriteLocal,
  seedLikedOverride,
} from "../../../store/slices/athleteSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp } from "@fortawesome/free-regular-svg-icons";
import Modal from "../../../components/Modal";
import AthleteScore from "../../../components/athlete-scores/athlete-score";
import CircularProgressBar from "../../../components/CircularProgressBar";
import AthleteProfileModal from "../../../components/AthleteProfileModal";

export default function AthleteChip({
  athlete,
  showStarButton,
  isAdmin = false,
  onAthleteUpdate,
}) {
  const [isAthleteModalOpen, setIsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();

  const likedOverrides = useSelector((state) => state.athletes.likedOverrides);
  const isFavorite =
    athlete.userId in likedOverrides
      ? likedOverrides[athlete.userId]
      : athlete.liked;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 640);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const handleStarClick = async () => {
    const shouldLike = !isFavorite;
    dispatch(
      toggleFavouriteLocal({
        athleteUserId: athlete.userId,
        liked: shouldLike,
      }),
    );
    try {
      await likeAthlete(apiInstance, shouldLike, athlete.userId);
    } catch (err) {
      dispatch(
        toggleFavouriteLocal({
          athleteUserId: athlete.userId,
          liked: !shouldLike,
        }),
      );
      toast.error(`Error liking athlete`, { position: "top-center" });
    }
  };

  const getCircleBackgroundColor = () => {
    return isFavorite ? "bg-yellow-500" : "bg-gray-400";
  };

  const handleViewClick = () => {
    dispatch(
      seedLikedOverride({ athleteUserId: athlete.userId, liked: isFavorite }),
    );
    setIsModalOpen(true);
  };

  const handleEditClick = () => {
    setIsProfileModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  return (
    <>
      <div
        className="flex justify-between items-center border-red-500 border rounded-md px-3 py-3 mb-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-0 w-full">
          <div className="h-[40px] w-[40px] my-auto overflow-hidden rounded-md">
            <img
              alt={`${athlete.name} ${athlete.lastname}`}
              src={athlete.profilePicture || "/default-profile-img.png"}
              className="object-cover h-full w-full"
            />
          </div>
          <div className="ml-2 flex-1">
            <p className="text-base lg:text-lg uppercase truncate">
              {athlete.name} {athlete.lastname}
            </p>
            <p className="text-sm text-gray-700 truncate">
              {athlete.position || athlete.primaryPosition}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {showStarButton && (
              <button
                className="mr-2 xl:mr-6 text-[#8E8E93]"
                onClick={handleStarClick}
              >
                <div
                  className={`flex items-center justify-center h-[32px] xl:h-[40px] w-[32px] xl:w-[40px] rounded-full ${getCircleBackgroundColor()}`}
                >
                  <FontAwesomeIcon
                    icon={faThumbsUp}
                    style={{ color: "#fff" }}
                  />
                </div>
              </button>
            )}
            <div className="">
              {isAdmin ? (
                <CircularProgressBar
                  progress={athlete.positionScore || 0}
                  size={isMobile ? 38 : 48}
                  strokeWidth={4}
                  color="#1C4ED8"
                />
              ) : (
                <CircularProgressBar
                  progress={athlete.positionScore || 0}
                  size={isMobile ? 38 : 48}
                  strokeWidth={4}
                  color="#1C4ED8"
                />
              )}
            </div>
            <div className="flex gap-2 ml-2 md:ml-6">
              <button
                className="text-sm px-2 py-2 text-white bg-red-500 rounded uppercase my-auto hover:bg-red-700"
                onClick={handleViewClick}
              >
                View
              </button>

              {/* Edit button - only visible for admin users */}
              {isAdmin && (
                <button
                  className="text-lg px-2 py-2 cursor-pointer hover:text-red-700"
                  onClick={handleEditClick}
                >
                  <svg
                    className="h-5 w-5 text-gray-500 "
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 640"
                  >
                    {" "}
                    <path d="M100.4 417.2C104.5 402.6 112.2 389.3 123 378.5L304.2 197.3L338.1 163.4C354.7 180 389.4 214.7 442.1 267.4L476 301.3L442.1 335.2L260.9 516.4C250.2 527.1 236.8 534.9 222.2 539L94.4 574.6C86.1 576.9 77.1 574.6 71 568.4C64.9 562.2 62.6 553.3 64.9 545L100.4 417.2zM156 413.5C151.6 418.2 148.4 423.9 146.7 430.1L122.6 517L209.5 492.9C215.9 491.1 221.7 487.8 226.5 483.2L155.9 413.5zM510 267.4C493.4 250.8 458.7 216.1 406 163.4L372 129.5C398.5 103 413.4 88.1 416.9 84.6C430.4 71 448.8 63.4 468 63.4C487.2 63.4 505.6 71 519.1 84.6L554.8 120.3C568.4 133.9 576 152.3 576 171.4C576 190.5 568.4 209 554.8 222.5C551.3 226 536.4 240.9 509.9 267.4z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Athlete Score Modal */}
      <Modal
        isOpen={isAthleteModalOpen}
        onClose={handleCloseModal}
        backdrop="static"
        keyboard={false}
      >
        <AthleteScore
          userId={athlete.userId}
          showOffers={true}
          showTrackittScore={true}
          showPositionScore={true}
          searchEvent={false}
          showProfile={true}
          showRank={true}
          showLikes={true}
        />
      </Modal>

      {/* Athlete Profile Edit Modal */}
      {isProfileModalOpen && (
        <AthleteProfileModal
          athlete={athlete}
          onClose={handleCloseProfileModal}
          onSave={onAthleteUpdate}
        />
      )}
    </>
  );
}
